import type { FC } from 'react';
import { Navigate } from 'react-router-dom';
import type { PlainAction } from '@components/types';
import {
  EVAL_CONFIGS_QUERY_KEY,
  getEvalConfigsTableColumns,
  useSetChampionEvalConfigMutation,
} from '@features/eval-config';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModalDispatch } from '@hooks/useModalProvider';
import { fetchEvalConfigs } from '@queries/evalConfigs';
import { isSuperadmin } from '@root/helpers/permissions';
import { useUserStore } from '@src/containers/Auth/store';
import withMenuLayout from '@src/layout/withMenuLayout';
import Nav from '@src/routes';
import { useQuery } from '@tanstack/react-query';

import { MODALS } from '../../constants';

import { ReactTable } from './components';
import { useDataIsEmpty } from './hooks';

export const EvalConfigs: FC = () => {
  const getFeatureFlag = useFeatureFlags();
  const user = useUserStore((state) => state.user);
  const { isLoading: isAccessLoading, pbqaEval: hasPbqaAccess } = useFeatureAccess();
  const isEnabled = getFeatureFlag('EvalConfigs') && isSuperadmin(user) && hasPbqaAccess;

  const { showModal } = useModalDispatch();

  const {
    data = [],
    isLoading,
    status,
  } = useQuery({
    queryKey: EVAL_CONFIGS_QUERY_KEY,
    queryFn: fetchEvalConfigs,
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

  const { onSetChampion, settingChampionId } = useSetChampionEvalConfigMutation();

  const isEmpty = useDataIsEmpty(data, status);
  const columns = getEvalConfigsTableColumns({ onSetChampion, settingChampionId });

  const createEntityAction: PlainAction = {
    key: 'create',
    label: 'New eval config',
    onClick: () => showModal(MODALS.ADMIN_CREATE_EVAL_CONFIG, {}),
  };

  // Hold rendering while the allowlist check is in flight — redirecting
  // early would bounce allowlisted users to 404 before the answer arrives.
  if (isAccessLoading) {
    return null;
  }

  if (!isEnabled) {
    return <Navigate to={Nav.NotFound} replace />;
  }

  return (
    <ReactTable
      actions={[createEntityAction]}
      columns={columns}
      columnVisibility={{
        id: false,
        created_at: false,
        created_by: false,
        last_updated_by: false,
      }}
      columnPinning={{ right: ['view'] }}
      createEntityAction={createEntityAction}
      entityName="eval config"
      filteredData={data}
      initialSorting={[{ id: 'updated_at', desc: true }]}
      status={status}
      isEmpty={isEmpty}
      isLoading={isLoading}
    />
  );
};

export const UserEvalConfigsPage = withMenuLayout(EvalConfigs);
